import EastIcon from '@mui/icons-material/East';
import WestIcon from '@mui/icons-material/West';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Button } from '@ifs-gcp/components';

import { PageCard } from '@/components/page-card';
import { ACTIONS } from '@/constants/actions';
import { useAvailableActions } from '@/hooks/useAvailableActions';
import { useAuditStore } from '@/store/auditStore';
import { useRouterStore } from '@/store/routerStore';

import { SubmitAuditAction } from '../tabs-actions/submit-audit-action/submit-audit-action';
import styles from './tab-navigation.module.scss';
import { buildNavigationSequence } from './utils';

export const TabNavigation = () => {
  const { t } = useTranslation();

  const audit = useAuditStore((store) => store.audit)!;
  const selectedTabId = useRouterStore((store) => store.selectedTabId);
  const selectedTabCards = useRouterStore((store) => store.selectedTabCards);
  const setSelectedTabId = useRouterStore((store) => store.setSelectedTabId);
  const setSelectedTabCard = useRouterStore((store) => store.setSelectedTabCard);

  const sequence = useMemo(() => buildNavigationSequence(audit.templateHandler.schema), [audit]);

  const isActivePlainTab = (step: (typeof sequence)[number]) =>
    step.mainTabId === selectedTabId && !step.cardId;

  const isActiveModuleSubTab = (step: (typeof sequence)[number]) => {
    if (step.mainTabId !== selectedTabId || !step.cardId) {
      return false;
    }

    const activeSubTabId = selectedTabCards[step.cardId];

    if (!activeSubTabId) {
      return true;
    }

    return activeSubTabId === step.cardTabValue;
  };

  const currentIndex = useMemo(
    () => sequence.findIndex((step) => isActivePlainTab(step) || isActiveModuleSubTab(step)),
    [sequence, selectedTabId, selectedTabCards],
  );

  const navigateTo = (index: number) => {
    const step = sequence[index];

    setSelectedTabId(step.mainTabId);

    if (step.cardId && step.cardTabValue) {
      setSelectedTabCard(step.cardId, step.cardTabValue);
    }
  };

  const goToNext = () => {
    if (currentIndex < sequence.length - 1) {
      navigateTo(currentIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      navigateTo(currentIndex - 1);
    }
  };

  const { audit_case_uuid: auditCaseUuid } = useParams();
  const { availableActions } = useAvailableActions();

  const isFirst = currentIndex <= 0;
  const isLast = currentIndex >= sequence.length - 1;

  return (
    <div className={styles.container}>
      <PageCard>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<WestIcon />}
          disabled={isFirst}
          onClick={goToPrevious}
        >
          {t('actions.back')}
        </Button>

        {isLast && availableActions.has(ACTIONS.AUDIT_CASE_SUBMIT) && auditCaseUuid ? (
          <SubmitAuditAction auditCaseUuid={auditCaseUuid} />
        ) : (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<EastIcon />}
            disabled={isLast}
            onClick={goToNext}
          >
            {t('actions.next')}
          </Button>
        )}
      </PageCard>
    </div>
  );
};
